import { MutableRefObject, useCallback, useEffect } from 'react'

import { useHashUpdater } from '@editor/hooks/useHashUpdater'
import { CanvasConfig } from '@editor/interfaces'
import { matchesKey } from '@editor/services/Shortcuts'
import { useAppSelector } from '@editor/store/app'
import mixpanel from 'mixpanel-browser'
import { useLocation } from 'react-router'

const useDocumentEventListeners = (
  toolsHook: MutableRefObject<
    {
      key: string
      config: CanvasConfig
    }[]
  >,
) => {
  const location = useLocation()
  const { getHashValue } = useHashUpdater('it')

  // Store
  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const initCameraProfileCompleted = useAppSelector((state) => state.toolCameraProfile.initCompleted)

  /**
   * Handle undo event
   */
  const handleUndo = useCallback(() => {
    // Execute events for tools
    toolsHook.current.forEach((tool) => tool.config?.events?.onUndo && tool.config.events.onUndo())
    // Mixpanel tracking
    mixpanel.track('Undo', { Tool: selectedTool })
  }, [toolsHook, selectedTool])

  /**
   * Handle cancel event
   */
  const handleCancel = useCallback(() => {
    // Execute events for tools
    toolsHook.current.forEach((tool) => tool.config?.events?.onCancel && tool.config.events.onCancel())
  }, [toolsHook])

  /**
   * Handle complete event
   */
  const handleComplete = useCallback(() => {
    // Execute events for tools
    toolsHook.current.forEach((tool) => tool.config?.events?.onComplete && tool.config.events.onComplete())
  }, [toolsHook])

  /**
   * Handle key down events
   */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // on Ctrl + Z (Windows/Linux) or Cmd + Z (Mac), handle undo
      if (matchesKey(e, { key: 'z', ctrl: true }, { fallbackToMeta: true })) {
        handleUndo()
      }
    },
    [handleUndo],
  )

  /**
   * Handle inspection item focus event
   *
   * @param e Custom event with item ID in detail
   */
  const handleInspectionItemFocus = useCallback(
    (e: Event) => {
      const itemId = (e as CustomEvent<string>).detail
      toolsHook.current.forEach((tool) => tool.config?.events?.onFocusItem && tool.config.events.onFocusItem(itemId))
    },
    [toolsHook],
  )

  /**
   * Handle hash-based events, such as focusing on specific items or sections when the URL hash changes.
   */
  useEffect(() => {
    if (!initCameraProfileCompleted) return

    const itemId = getHashValue()
    if (!itemId) return

    toolsHook.current.forEach((tool) => tool.config?.events?.onFocusItem && tool.config.events.onFocusItem(itemId))
  }, [location.hash, initCameraProfileCompleted, toolsHook, getHashValue])

  /**
   * Add listener for keyboad shortcuts
   */
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('editor:tool-undo', handleUndo)
    document.addEventListener('editor:tool-cancel', handleCancel)
    document.addEventListener('editor:tool-complete', handleComplete)
    document.addEventListener('editor:inspection-item:focus', handleInspectionItemFocus)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('editor:tool-undo', handleUndo)
      document.removeEventListener('editor:tool-cancel', handleCancel)
      document.removeEventListener('editor:tool-complete', handleComplete)
      document.removeEventListener('editor:inspection-item:focus', handleInspectionItemFocus)
    }
  }, [handleKeyDown, handleUndo, handleCancel, handleComplete, handleInspectionItemFocus])
}

export default useDocumentEventListeners
